import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';
import type { SqlQueryResultDto, SavedSqlScriptDto, CreateSavedSqlScriptRequest } from '../../services/adminApi.types';
import './SqlConsoleTab.css';

interface Tab {
    id: string;
    name: string;
    query: string;
    result: SqlQueryResultDto | null;
    savedScriptId?: number;
}

// Helper function to detect if a query is destructive
// Handles queries with leading comments to match backend validation
const isDestructiveQuery = (sql: string): boolean => {
    // Remove leading comments and whitespace to prevent detection bypass
    // Handles both /* ... */ and -- comments
    const cleaned = sql.trim()
        .toUpperCase()
        .replace(/^\s*(\/\*.*?\*\/|--[^\n]*\n)*\s*/g, '');

    return cleaned.startsWith('INSERT')
        || cleaned.startsWith('UPDATE')
        || cleaned.startsWith('DELETE')
        || cleaned.startsWith('CREATE')
        || cleaned.startsWith('DROP')
        || cleaned.startsWith('ALTER')
        || cleaned.startsWith('TRUNCATE');
};

export const SqlConsoleTab: React.FC = () => {
    const [tabs, setTabs] = useState<Tab[]>([
        { id: 'tab-1', name: 'Query 1', query: '', result: null }
    ]);
    const [activeTabId, setActiveTabId] = useState('tab-1');
    const [isLoading, setIsLoading] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [savedScripts, setSavedScripts] = useState<SavedSqlScriptDto[]>([]);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [saveDialogName, setSaveDialogName] = useState('');
    const [isSavingScript, setIsSavingScript] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingQuery, setPendingQuery] = useState<string | null>(null);

    const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];

    // Load saved scripts on mount
    useEffect(() => {
        loadSavedScripts();
    }, []);

    const loadSavedScripts = async () => {
        try {
            const scripts = await adminApi.getSavedScripts();
            // Sort scripts alphabetically by name (case-insensitive)
            const sortedScripts = scripts.sort((a, b) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            );
            setSavedScripts(sortedScripts);
        } catch (error) {
            console.error('Failed to load saved scripts:', error);
        }
    };

    // Extracted query execution logic to avoid recursive calls
    const executeQuery = async (confirmed = false) => {
        setIsLoading(true);

        try {
            const queryResult = await adminApi.executeSqlQuery(activeTab.query, confirmed);

            // Update the result for this tab
            setTabs(prevTabs =>
                prevTabs.map(tab =>
                    tab.id === activeTabId ? { ...tab, result: queryResult } : tab
                )
            );
        } catch (error) {
            console.error('Failed to execute SQL query:', error);

            // Update with error result
            setTabs(prevTabs =>
                prevTabs.map(tab =>
                    tab.id === activeTabId
                        ? {
                            ...tab,
                            result: {
                                columns: [],
                                data: [],
                                error: error instanceof Error ? error.message : 'Failed to execute query',
                                rowCount: 0
                            }
                        }
                        : tab
                )
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleExecute = async () => {
        if (!activeTab.query.trim()) {
            return;
        }

        // Check if query is destructive and requires confirmation
        if (isDestructiveQuery(activeTab.query)) {
            setPendingQuery(activeTab.query);
            setShowConfirmDialog(true);
            return;
        }

        // Execute non-destructive query immediately
        await executeQuery(false);
    };

    const handleConfirmExecute = () => {
        setShowConfirmDialog(false);
        setPendingQuery(null);
        executeQuery(true);
    };

    const handleCancelExecute = () => {
        setShowConfirmDialog(false);
        setPendingQuery(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Cmd+Enter or Ctrl+Enter to execute
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleExecute();
        }
    };

    const formatResultAsMarkdown = (queryText: string, queryResult: SqlQueryResultDto): string => {
        if (queryResult.error) {
            return `## SQL Query\n\n\`\`\`sql\n${queryText}\n\`\`\`\n\n## Error\n\n${queryResult.error}`;
        }

        if (queryResult.rowCount === 0) {
            return `## SQL Query\n\n\`\`\`sql\n${queryText}\n\`\`\`\n\n## Results\n\nNo rows returned.`;
        }

        // Build markdown table
        let markdown = `## SQL Query\n\n\`\`\`sql\n${queryText}\n\`\`\`\n\n## Results\n\n`;
        markdown += `${queryResult.rowCount} ${queryResult.rowCount === 1 ? 'row' : 'rows'}\n\n`;

        // Table header
        markdown += '| ' + queryResult.columns.join(' | ') + ' |\n';
        markdown += '| ' + queryResult.columns.map(() => '---').join(' | ') + ' |\n';

        // Table rows
        for (const row of queryResult.data) {
            const cells = row.map(cell => {
                if (cell === null) {
                    return 'NULL';
                }
                // Escape pipe characters and format cell content
                const cellStr = String(cell).replace(/\|/g, '\\|').replace(/\n/g, ' ');
                return cellStr;
            });
            markdown += '| ' + cells.join(' | ') + ' |\n';
        }

        return markdown;
    };

    const handleCopyToClipboard = async () => {
        if (!activeTab.result || !activeTab.query) {
            return;
        }

        try {
            const markdown = formatResultAsMarkdown(activeTab.query, activeTab.result);
            await navigator.clipboard.writeText(markdown);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            alert('Failed to copy to clipboard. Please try again.');
        }
    };

    const addNewTab = () => {
        const newTabNumber = tabs.length + 1;
        const newTab: Tab = {
            id: `tab-${Date.now()}`,
            name: `Query ${newTabNumber}`,
            query: '',
            result: null
        };
        setTabs([...tabs, newTab]);
        setActiveTabId(newTab.id);
    };

    const closeTab = (tabId: string) => {
        if (tabs.length === 1) {
            return; // Keep at least one tab
        }

        const tabIndex = tabs.findIndex(t => t.id === tabId);
        const newTabs = tabs.filter(t => t.id !== tabId);
        setTabs(newTabs);

        if (activeTabId === tabId) {
            // Switch to adjacent tab
            const newActiveIndex = Math.max(0, tabIndex - 1);
            setActiveTabId(newTabs[newActiveIndex].id);
        }
    };

    const updateQuery = (query: string) => {
        setTabs(prevTabs =>
            prevTabs.map(tab =>
                tab.id === activeTabId ? { ...tab, query } : tab
            )
        );
    };

    const renameTab = (tabId: string, newName: string) => {
        setTabs(prevTabs =>
            prevTabs.map(tab =>
                tab.id === tabId ? { ...tab, name: newName } : tab
            )
        );
    };

    const loadSavedScript = (script: SavedSqlScriptDto) => {
        // Create new tab with the saved script
        const newTab: Tab = {
            id: `tab-${Date.now()}`,
            name: script.name,
            query: script.query,
            result: null,
            savedScriptId: script.id
        };
        setTabs([...tabs, newTab]);
        setActiveTabId(newTab.id);
    };

    const handleSaveScript = async () => {
        if (!saveDialogName.trim()) {
            return;
        }

        setIsSavingScript(true);

        try {
            const request: CreateSavedSqlScriptRequest = {
                name: saveDialogName.trim(),
                query: activeTab.query
            };

            const savedScript = await adminApi.createSavedScript(request);

            // Update current tab to be associated with the saved script
            setTabs(prevTabs =>
                prevTabs.map(tab =>
                    tab.id === activeTabId
                        ? { ...tab, savedScriptId: savedScript.id, name: savedScript.name }
                        : tab
                )
            );

            // Reload saved scripts list
            await loadSavedScripts();

            // Close dialog
            setShowSaveDialog(false);
            setSaveDialogName('');
        } catch (error) {
            console.error('Failed to save script:', error);
            alert('Failed to save script');
        } finally {
            setIsSavingScript(false);
        }
    };

    const handleUpdateScript = async () => {
        if (!activeTab.savedScriptId) {
            return;
        }

        try {
            await adminApi.updateSavedScript(activeTab.savedScriptId, {
                name: activeTab.name,
                query: activeTab.query
            });

            // Reload saved scripts list
            await loadSavedScripts();

            alert('Script updated successfully');
        } catch (error) {
            console.error('Failed to update script:', error);
            alert('Failed to update script');
        }
    };

    const handleDeleteScript = async (scriptId: number) => {
        if (!confirm('Are you sure you want to delete this saved script?')) {
            return;
        }

        try {
            await adminApi.deleteSavedScript(scriptId);

            // Remove the saved script ID from any tabs using it
            setTabs(prevTabs =>
                prevTabs.map(tab =>
                    tab.savedScriptId === scriptId
                        ? { ...tab, savedScriptId: undefined }
                        : tab
                )
            );

            // Reload saved scripts list
            await loadSavedScripts();
        } catch (error) {
            console.error('Failed to delete script:', error);
            alert('Failed to delete script');
        }
    };

    return (
        <div className="sql-console-container">
            <div className="sql-console-header">
                <h2>SQL Console</h2>
                <p className="sql-console-hint">
                    Execute SQL queries to inspect and modify the database. Read operations (SELECT, EXPLAIN, etc.) execute immediately.
                    Destructive operations (INSERT, UPDATE, DELETE, etc.) require confirmation. Press Cmd/Ctrl+Enter to execute.
                </p>
            </div>

            <div className="sql-console-layout">
                {/* Saved Scripts Sidebar */}
                <div className="sql-saved-scripts-sidebar">
                    <h3>Saved Scripts</h3>
                    {savedScripts.length === 0 ? (
                        <p className="sql-no-scripts">No saved scripts yet</p>
                    ) : (
                        <div className="sql-saved-scripts-list">
                            {savedScripts.map(script => (
                                <div key={script.id} className="sql-saved-script-item">
                                    <div
                                        className="sql-saved-script-load"
                                        onClick={() => loadSavedScript(script)}
                                        title={`Load "${script.name}"`}
                                    >
                                        <span className="sql-saved-script-name">{script.name}</span>
                                        <button
                                            className="sql-saved-script-delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteScript(script.id);
                                            }}
                                            title="Delete script"
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Main Console Area */}
                <div className="sql-console-main">
                    {/* Tabs */}
                    <div className="sql-tabs">
                        {tabs.map(tab => (
                            <div
                                key={tab.id}
                                className={`sql-tab ${tab.id === activeTabId ? 'sql-tab-active' : ''}`}
                            >
                                <input
                                    type="text"
                                    className="sql-tab-name"
                                    value={tab.name}
                                    onChange={(e) => renameTab(tab.id, e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                />
                                <div className="sql-tab-switch" onClick={() => setActiveTabId(tab.id)}>
                                    <span className="sql-tab-label">{tab.name}</span>
                                    {tabs.length > 1 && (
                                        <button
                                            className="sql-tab-close"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                closeTab(tab.id);
                                            }}
                                            title="Close tab"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        <button className="sql-tab-add" onClick={addNewTab} title="New tab">
                            +
                        </button>
                    </div>

                    {/* Query Editor */}
                    <div className="sql-query-section">
                        <textarea
                            className="sql-query-input"
                            placeholder="SELECT * FROM users LIMIT 10;"
                            value={activeTab.query}
                            onChange={(e) => updateQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            rows={6}
                        />
                        <div className="sql-query-actions">
                            <button
                                className="sql-execute-btn"
                                onClick={() => handleExecute()}
                                disabled={isLoading || !activeTab.query.trim()}
                            >
                                {isLoading ? 'Executing...' : 'Execute'}
                            </button>
                            {activeTab.savedScriptId ? (
                                <button
                                    className="sql-update-btn"
                                    onClick={handleUpdateScript}
                                    disabled={!activeTab.query.trim()}
                                >
                                    Update Saved Script
                                </button>
                            ) : (
                                <button
                                    className="sql-save-btn"
                                    onClick={() => setShowSaveDialog(true)}
                                    disabled={!activeTab.query.trim()}
                                >
                                    Save Script
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Results */}
                    {activeTab.result && (
                        <div className="sql-results-section">
                            {activeTab.result.error ? (
                                <div className="sql-error">{activeTab.result.error}</div>
                            ) : (
                                <>
                                    <div className="sql-results-header">
                                        <span className="sql-row-count">
                                            {activeTab.result.columns.length === 0
                                                ? `${activeTab.result.rowCount} ${activeTab.result.rowCount === 1 ? 'row' : 'rows'} affected`
                                                : `${activeTab.result.rowCount} ${activeTab.result.rowCount === 1 ? 'row' : 'rows'}`}
                                        </span>
                                        <button
                                            className="sql-copy-btn"
                                            onClick={handleCopyToClipboard}
                                            title="Copy query and results as markdown"
                                        >
                                            {copySuccess ? (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="20 6 9 17 4 12"></polyline>
                                                    </svg>
                                                    <span>Copied!</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                    </svg>
                                                    <span>Copy as Markdown</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    {activeTab.result.rowCount > 0 && (
                                        <div className="sql-table-container">
                                            <table className="sql-results-table">
                                                <thead>
                                                    <tr>
                                                        {activeTab.result.columns.map((column) => (
                                                            <th key={column}>{column}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {activeTab.result.data.map((row, rowIndex) => (
                                                        <tr key={rowIndex}>
                                                            {row.map((cell, cellIndex) => (
                                                                <td key={cellIndex}>
                                                                    {cell === null ? (
                                                                        <span className="sql-null">NULL</span>
                                                                    ) : (
                                                                        String(cell)
                                                                    )}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Save Dialog */}
            {showSaveDialog && (
                <div className="sql-dialog-overlay" onClick={() => setShowSaveDialog(false)}>
                    <div className="sql-dialog" onClick={(e) => e.stopPropagation()}>
                        <h3>Save SQL Script</h3>
                        <input
                            type="text"
                            className="sql-dialog-input"
                            placeholder="Script name (e.g., Active Users Count)"
                            value={saveDialogName}
                            onChange={(e) => setSaveDialogName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveScript();
                                } else if (e.key === 'Escape') {
                                    setShowSaveDialog(false);
                                }
                            }}
                            autoFocus
                        />
                        <div className="sql-dialog-actions">
                            <button
                                className="sql-dialog-btn sql-dialog-btn-cancel"
                                onClick={() => setShowSaveDialog(false)}
                                disabled={isSavingScript}
                            >
                                Cancel
                            </button>
                            <button
                                className="sql-dialog-btn sql-dialog-btn-primary"
                                onClick={handleSaveScript}
                                disabled={!saveDialogName.trim() || isSavingScript}
                            >
                                {isSavingScript ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Dialog for Destructive Operations */}
            {showConfirmDialog && (
                <div
                    className="sql-dialog-overlay"
                    onClick={handleCancelExecute}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            handleCancelExecute();
                        }
                    }}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="confirm-dialog-title"
                >
                    <div className="sql-dialog" onClick={(e) => e.stopPropagation()}>
                        <h3 id="confirm-dialog-title">⚠️ Confirm Destructive Operation</h3>
                        <p className="sql-confirm-warning">
                            This query appears to be a destructive operation that will modify the database.
                            Are you sure you want to execute it?
                        </p>
                        {pendingQuery && (
                            <pre className="sql-confirm-query">{pendingQuery}</pre>
                        )}
                        <div className="sql-dialog-actions">
                            <button
                                className="sql-dialog-btn sql-dialog-btn-cancel"
                                onClick={handleCancelExecute}
                                autoFocus
                            >
                                Cancel
                            </button>
                            <button
                                className="sql-dialog-btn sql-dialog-btn-danger"
                                onClick={handleConfirmExecute}
                            >
                                Execute
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
